/**
 * @fileoverview Deterministic Zcash seed derivation from a Vultisig vault's
 * identity material (HKDF-SHA256). Byte-compatible with WINBIT32's
 * `crossChainDerivation.js` so the same vault lands on the same seed — and
 * therefore the same ZIP-32 seed fingerprint — in both apps.
 *
 * For Orchard FROST co-signing the spend authority is the threshold key
 * (group_public = `ak`), NOT this seed. The derived seed is used only to
 * produce a stable `seedFingerprintHex` for the PCZT's `zip32_derivation`
 * metadata (see `tools/orchard-scanner/src/pczt_builder.rs`, which writes it
 * into the proprietary field and never derives keys from it). Deriving it the
 * same way as WINBIT32 keeps wallet identity consistent across the two apps.
 *
 * Pure Web Crypto, no host/React coupling — part of the reusable module.
 */

import { hexToBytes } from './bytes';

// Domain-separation constants — MUST match WINBIT32 crossChainDerivation.js.
const ZCASH_VAULT_SALT = 'zcash-shielded-seed:v3:vault-identity';
const ZCASH_VAULT_INFO_PREFIX = 'winbit32-zcash-orchard:';
const DERIVED_SEED_BITS = 512; // 64 bytes — same length as a BIP-39 seed.

const getSubtle = (): SubtleCrypto => {
	const c = (typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined);
	if (!c?.subtle) {
		throw new Error('Web Crypto (crypto.subtle) is required for vault seed derivation (HTTPS context).');
	}
	return c.subtle;
};

/**
 * HKDF-SHA256 expand arbitrary input key material into a 64-byte seed.
 *
 * @param ikm  Input key material (e.g. the vault chain code bytes).
 * @param salt Domain-specific salt.
 * @param info Context binding.
 */
export const deriveCrossChainSeed = async (
	ikm: Uint8Array,
	salt: Uint8Array,
	info: Uint8Array,
): Promise<Uint8Array> => {
	if (!ikm || ikm.length === 0) throw new Error('deriveCrossChainSeed: IKM must be non-empty');
	const subtle = getSubtle();
	const keyMaterial = await subtle.importKey('raw', ikm, { name: 'HKDF' }, false, ['deriveBits']);
	const derived = await subtle.deriveBits(
		{ name: 'HKDF', hash: 'SHA-256', salt, info },
		keyMaterial,
		DERIVED_SEED_BITS,
	);
	return new Uint8Array(derived);
};

/**
 * Derive a 64-byte Zcash seed from a Vultisig vault's identity
 * (BIP-32 chain code + ECDSA public key). Matches WINBIT32 exactly.
 *
 * @param hexChainCode Vault BIP-32 chain code (hex, ≥ 32 bytes).
 * @param publicKeyHex Vault ECDSA public key (hex), used for domain separation.
 */
export const deriveZcashSeedFromVaultIdentity = async (
	hexChainCode: string,
	publicKeyHex: string,
): Promise<Uint8Array> => {
	if (!hexChainCode || typeof hexChainCode !== 'string') {
		throw new Error('Vault chain code required for Zcash seed derivation.');
	}
	if (!publicKeyHex || typeof publicKeyHex !== 'string') {
		throw new Error('Vault public key required for Zcash seed derivation.');
	}
	const ikm = hexToBytes(hexChainCode.replace(/^0x/i, ''));
	if (ikm.length < 32) {
		throw new Error(`Vault chain code too short (${ikm.length} bytes, need ≥ 32).`);
	}
	const enc = new TextEncoder();
	return deriveCrossChainSeed(
		ikm,
		enc.encode(ZCASH_VAULT_SALT),
		enc.encode(ZCASH_VAULT_INFO_PREFIX + publicKeyHex),
	);
};
